import { useState, type ReactNode } from "react";
import { AlertTriangle, Check, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { toneSoftClasses } from "@/lib/tones";
import type { ArranjoFaturamento, OmieEmpresaChave, PropostaItem } from "@/types/finance";
import {
  ARRANJOS,
  arranjoDaEmpresa,
  escolhaDoItem,
  fraseDaNota,
  mesmaEscolha,
  nomeDaEmpresa,
  patchDoArranjo,
  tituloDoArranjo,
  type Empresas,
  type EscolhaDeArranjo,
  type PatchDeFaturamento,
} from "./arranjo-de-faturamento";
import { useMoedaDaProposta } from "@/components/finance/proposta/moeda-da-proposta";

interface SeletorDeArranjoProps {
  itens: PropostaItem[];
  empresas: Empresas;
  disabled?: boolean;
  onAplicar: (patch: PatchDeFaturamento) => void;
  /** Os quatro campos detalhados, para uma combinação fora dos arranjos. */
  personalizar: ReactNode;
  /** Arranjo salvo no cadastro da agência. */
  padrao?: EscolhaDeArranjo | null;
  nomeDaAgencia?: string;
  onSalvarComoPadrao?: (escolha: EscolhaDeArranjo) => void;
  compacto?: boolean;
}

export function SeletorDeArranjo({
  itens,
  empresas,
  disabled,
  onAplicar,
  personalizar,
  padrao,
  nomeDaAgencia,
  onSalvarComoPadrao,
  compacto,
}: SeletorDeArranjoProps) {
  const { moeda } = useMoedaDaProposta();
  const escolhas = itens.map(escolhaDoItem);
  const empresasDosItens = new Set(itens.map((item) => item.empresa_agencia?.chave ?? "corisco"));
  const empresaComum = empresasDosItens.size === 1 ? [...empresasDosItens][0] : null;
  const escolhaComum = escolhas.every((escolha) => mesmaEscolha(escolha, escolhas[0])) ? escolhas[0] : null;
  const personalizado = escolhas.some((escolha) => escolha === null);
  const [detalhesAbertos, setDetalhesAbertos] = useState(personalizado && itens.length === 1);
  const foraDoPadrao = !!padrao && escolhas.some((escolha) => !mesmaEscolha(escolha, padrao));
  const empresaExibida = nomeDaEmpresa(empresaComum);

  const escolherEmpresa = (chave: OmieEmpresaChave) =>
    onAplicar(patchDoArranjo({ empresa: chave, arranjo: escolhaComum?.arranjo ?? arranjoDaEmpresa(chave, empresas) }, empresas));
  const escolherArranjo = (arranjo: ArranjoFaturamento) =>
    onAplicar(patchDoArranjo({ empresa: empresaComum ?? "corisco", arranjo }, empresas));

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-muted-foreground">Quem fatura a agência</span>
        <Select value={empresaComum ?? undefined} disabled={disabled} onValueChange={(valor) => escolherEmpresa(valor as OmieEmpresaChave)}>
          <SelectTrigger aria-label="Empresa que fatura a agência" className="h-8 w-auto min-w-[130px] gap-2 bg-background text-xs">
            <SelectValue placeholder="Varia por período" />
          </SelectTrigger>
          <SelectContent>
            {empresas.map((empresa) => (
              <SelectItem key={empresa.chave} value={empresa.chave} className="text-xs">
                {nomeDaEmpresa(empresa.chave)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div role="radiogroup" aria-label="Arranjo de faturamento" className={cn("grid gap-2", !compacto && "sm:grid-cols-3")}>
        {ARRANJOS.map((opcao) => {
          const marcado = escolhaComum?.arranjo === opcao.valor;
          return (
            <button
              key={opcao.valor}
              type="button"
              role="radio"
              aria-checked={marcado}
              disabled={disabled}
              onClick={() => escolherArranjo(opcao.valor)}
              className={cn(
                "flex items-start gap-2 rounded-md border bg-background px-3 py-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60",
                marcado ? "border-primary ring-1 ring-primary" : "hover:border-foreground/30",
              )}
            >
              <span
                className={cn(
                  "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border",
                  marcado && "border-primary bg-primary text-primary-foreground",
                )}
              >
                {marcado && <Check className="h-3 w-3" />}
              </span>
              <span className="space-y-0.5">
                <span className="block text-xs font-medium">{opcao.titulo}</span>
                <span className="block text-[11px] leading-snug text-muted-foreground">{opcao.descricao(empresaExibida)}</span>
              </span>
            </button>
          );
        })}
      </div>
      {itens.length === 1 && <p className="text-xs">{fraseDaNota(itens[0], moeda)}</p>}
      {itens.length > 1 && !escolhaComum && (
        <p className="text-xs text-muted-foreground">Os períodos estão com arranjos diferentes. Escolha um para aplicar em todos.</p>
      )}
      {foraDoPadrao && padrao && (
        <div className={cn("flex flex-wrap items-center gap-2 rounded-md px-3 py-2 text-xs", toneSoftClasses.warning)}>
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          <span className="flex-1">
            {nomeDaAgencia ? `A ${nomeDaAgencia}` : "A agência"} está cadastrada como: {nomeDaEmpresa(padrao.empresa)},{" "}
            {tituloDoArranjo(padrao.arranjo).toLowerCase()}.
          </span>
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={disabled} onClick={() => onAplicar(patchDoArranjo(padrao, empresas))}>
            Usar o cadastro
          </Button>
          {onSalvarComoPadrao && escolhaComum && (
            <Button size="sm" variant="ghost" className="h-7 text-xs" disabled={disabled} onClick={() => onSalvarComoPadrao(escolhaComum)}>
              Salvar este como padrão
            </Button>
          )}
        </div>
      )}
      {!padrao && onSalvarComoPadrao && escolhaComum && (
        <button
          type="button"
          disabled={disabled}
          onClick={() => onSalvarComoPadrao(escolhaComum)}
          className="text-xs text-primary underline-offset-2 hover:underline disabled:opacity-60"
        >
          Salvar como padrão {nomeDaAgencia ? `da ${nomeDaAgencia}` : "da agência"} para as próximas propostas
        </button>
      )}
      <div>
        <button
          type="button"
          onClick={() => setDetalhesAbertos((aberto) => !aberto)}
          className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
          aria-expanded={detalhesAbertos}
        >
          <ChevronDown className={cn("h-3 w-3 transition-transform", detalhesAbertos && "rotate-180")} />
          {personalizado ? "Combinação personalizada: ver os campos" : "Personalizar os campos"}
        </button>
        {detalhesAbertos && <div className="mt-2">{personalizar}</div>}
      </div>
    </div>
  );
}
